import { supabaseClient as supabase } from '../supabase'
import { Database } from 'types/supabase'

type Campaign = Database['public']['Tables']['campaigns']['Row']
type CampaignStatus = 'Draft' | 'Scheduled' | 'Active' | 'Paused' | 'Ended'

export interface StatusUpdatePayload {
  status: CampaignStatus
  manually_paused?: boolean
}

export const validateStatusTransition = (
  currentStatus: CampaignStatus,
  newStatus: CampaignStatus
): { valid: boolean; message?: string } => {
  const validTransitions: Record<CampaignStatus, CampaignStatus[]> = {
    'Draft': ['Scheduled', 'Ended'],
    'Scheduled': ['Active', 'Ended'],
    'Active': ['Paused', 'Ended'],
    'Paused': ['Active', 'Ended'],
    'Ended': []
  }

  if (currentStatus === newStatus) {
    return { valid: false, message: 'Campaign is already in this status' }
  }

  if (!validTransitions[currentStatus].includes(newStatus)) {
    return {
      valid: false,
      message: `Cannot transition from ${currentStatus} to ${newStatus}`
    }
  }

  return { valid: true }
}

export const updateCampaignStatus = async (
  campaignId: string,
  payload: StatusUpdatePayload
): Promise<Campaign | null> => {
  const { data: campaign, error: fetchError } = await supabase
    .from('campaigns')
    .select('*, campaign_creatives(id)')
    .eq('id', campaignId)
    .single()

  if (fetchError || !campaign) {
    throw new Error('Campaign not found')
  }

  // Validate status transition
  const validation = validateStatusTransition(
    campaign.status as CampaignStatus,
    payload.status
  )

  if (!validation.valid) {
    throw new Error(validation.message)
  }

  // Check for creatives if activating
  if (payload.status === 'Active' && (!campaign.campaign_creatives || campaign.campaign_creatives.length === 0)) {
    throw new Error('Cannot activate campaign without assigned creatives')
  }

  // Check dates if activating
  if (payload.status === 'Active') {
    const now = new Date()
    const startDate = new Date(campaign.start_date)
    const endDate = new Date(campaign.end_date)

    // Check if end date is in the past
    if (endDate < now) {
      throw new Error('Cannot activate campaign with end date in the past')
    }

    // Check if current time is before start date
    if (startDate > now) {
      throw new Error('Cannot activate campaign before its start date. Campaign will auto-activate when the start date is reached.')
    }
  }

  // Update the campaign status
  const { data, error } = await supabase
    .from('campaigns')
    .update({
      status: payload.status,
      manually_paused: payload.status === 'Paused' ? true : false,
      updated_at: new Date().toISOString()
    })
    .eq('id', campaignId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data
}

export const bulkUpdateCampaignStatus = async (
  campaignIds: string[],
  payload: StatusUpdatePayload
): Promise<{ success: string[]; failed: Array<{ id: string; error: string }> }> => {
  const results = {
    success: [] as string[],
    failed: [] as Array<{ id: string; error: string }>
  }

  for (const id of campaignIds) {
    try {
      await updateCampaignStatus(id, payload)
      results.success.push(id)
    } catch (error) {
      results.failed.push({
        id,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  return results
}

export const autoTransitionCampaignStatuses = async (): Promise<void> => {
  const now = new Date()

  // Auto-activate scheduled campaigns that have reached their start date
  const { data: toActivate } = await supabase
    .from('campaigns')
    .select('id')
    .eq('status', 'Scheduled')
    .lte('start_date', now.toISOString())
    .gte('end_date', now.toISOString())

  if (toActivate && toActivate.length > 0) {
    for (const campaign of toActivate) {
      try {
        await updateCampaignStatus(campaign.id, { status: 'Active' })
      } catch (error) {
        console.error(`Failed to auto-activate campaign ${campaign.id}:`, error)
      }
    }
  }

  // Auto-end expired campaigns
  const { data: toEnd } = await supabase
    .from('campaigns')
    .select('id')
    .in('status', ['Active', 'Paused', 'Scheduled'])
    .lt('end_date', now.toISOString())

  if (toEnd && toEnd.length > 0) {
    for (const campaign of toEnd) {
      try {
        await updateCampaignStatus(campaign.id, { status: 'Ended' })
      } catch (error) {
        console.error(`Failed to auto-end campaign ${campaign.id}:`, error)
      }
    }
  }
}

export const getCampaignStatusHistory = async (
  campaignId: string
): Promise<any[]> => {
  const { data, error } = await supabase
    .from('campaigns')
    .select('status_history')
    .eq('id', campaignId)
    .single()

  if (error) {
    throw error
  }

  return data?.status_history || []
}